/**
 * <HeroVideo /> — reusable portrait 9:16 hero video player used across
 * Service pages and Product pages. Detects direct MP4/WebM (autoplay video),
 * YouTube, Vimeo, Bunny Stream (mediadelivery.net) and falls back to a
 * generic <iframe> for anything else.
 *
 * `loop` is intentionally OFF by default — Bunny Stream (and other hosts) let
 * admins toggle loop from their own dashboard, and forcing it in the URL
 * overrides their choice. Pass `loop` explicitly when a looping reel is
 * actually required.
 */
export default function HeroVideo({ src, testid = "hero-video", loop = false }) {
  const isDirectFile = /\.(mp4|webm|mov|m4v|ogg)(\?|$)/i.test(src);
  const isYouTube = /(?:youtube\.com|youtu\.be)/i.test(src);
  const isVimeo = /vimeo\.com/i.test(src);
  const isBunny = /mediadelivery\.net/i.test(src);

  if (isDirectFile) {
    return (
      <video
        src={src}
        autoPlay muted {...(loop ? { loop: true } : {})} playsInline
        className="absolute inset-0 w-full h-full object-cover"
        data-testid={testid}
      />
    );
  }

  if (isBunny) {
    const m = src.match(/mediadelivery\.net\/(?:play|embed)\/(\d+)\/([\w-]+)/i);
    if (!m) return null;
    const [, lib, id] = m;
    // NOTE: no `loop=` in the URL — Bunny's dashboard toggle decides that.
    const embed = `https://iframe.mediadelivery.net/embed/${lib}/${id}?autoplay=true&muted=true&preload=true&responsive=true`;
    return (
      <iframe
        title="Hero video"
        src={embed}
        loading="lazy"
        allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        data-testid={testid}
      />
    );
  }

  if (isYouTube) {
    const idMatch = src.match(/(?:v=|youtu\.be\/|shorts\/|embed\/)([\w-]{6,})/);
    const id = idMatch ? idMatch[1] : "";
    if (!id) return null;
    const loopParams = loop ? `&loop=1&playlist=${id}` : "";
    return (
      <iframe
        title="Hero video"
        src={`https://www.youtube.com/embed/${id}?autoplay=1&mute=1&controls=0&playsinline=1&modestbranding=1&rel=0${loopParams}`}
        allow="autoplay; encrypted-media; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        data-testid={testid}
      />
    );
  }

  if (isVimeo) {
    const idMatch = src.match(/vimeo\.com\/(?:video\/)?(\d+)/);
    const id = idMatch ? idMatch[1] : "";
    if (!id) return null;
    const loopParam = loop ? "&loop=1" : "";
    return (
      <iframe
        title="Hero video"
        src={`https://player.vimeo.com/video/${id}?autoplay=1&muted=1&background=1&controls=0${loopParam}`}
        allow="autoplay; picture-in-picture"
        allowFullScreen
        className="absolute inset-0 w-full h-full"
        data-testid={testid}
      />
    );
  }

  return (
    <iframe
      title="Hero video"
      src={src}
      loading="lazy"
      allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture"
      allowFullScreen
      className="absolute inset-0 w-full h-full"
      data-testid={testid}
    />
  );
}
